/** Places `id` directly after `afterId`. A null anchor places it first; an anchor that no longer exists places it last. */
export const insertIdAfter = (order: string[], id: string, afterId: string | null): string[] => {
  const orderWithoutId = order.filter((existingId) => existingId !== id);
  if (afterId === null) {
    return [id, ...orderWithoutId];
  }
  const anchorIndex = orderWithoutId.indexOf(afterId);
  if (anchorIndex === -1) {
    return [...orderWithoutId, id];
  }
  return [...orderWithoutId.slice(0, anchorIndex + 1), id, ...orderWithoutId.slice(anchorIndex + 1)];
};

/** The id that directly precedes `id` in `order`, or null when `id` is first. */
export const getPrecedingId = (order: string[], id: string): string | null => {
  const index = order.indexOf(id);
  return index > 0 ? order[index - 1] : null;
};

/**
 * Makes a requested order safe to adopt when its author may not have seen every concurrent add
 * and remove: ids that no longer exist are dropped, duplicates collapse to their first position,
 * and existing ids the request left out keep their place relative to the previous order.
 */
export const reconcileRequestedOrder = (requestedOrder: string[], previousOrder: string[], existingIds: ReadonlySet<string>): string[] => {
  const reconciledOrder = [...new Set(requestedOrder)].filter((id) => existingIds.has(id));
  const placedIds = new Set(reconciledOrder);

  for (const [previousIndex, id] of previousOrder.entries()) {
    if (!existingIds.has(id) || placedIds.has(id)) {
      continue;
    }
    const anchorId = previousOrder
      .slice(0, previousIndex)
      .reverse()
      .find((precedingId) => placedIds.has(precedingId));
    const insertIndex = anchorId === undefined ? 0 : reconciledOrder.indexOf(anchorId) + 1;
    reconciledOrder.splice(insertIndex, 0, id);
    placedIds.add(id);
  }

  return reconciledOrder;
};
